/* ============================================================
   SHE'S GOLDEN — Theme JavaScript
   ============================================================ */

(function () {
  'use strict';

  /* ============================================================
     UTILITIES
     ============================================================ */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

  function formatMoney(cents) {
    const fmt = window.ShesGolden?.moneyFormat || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    return fmt.replace('{{amount}}', amount).replace('{{amount_no_decimals}}', Math.round(cents / 100));
  }

  function trapFocus(el) {
    const focusable = $$('a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])', el)
      .filter(f => !f.hasAttribute('disabled') && !f.getAttribute('aria-hidden'));
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    el.addEventListener('keydown', e => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey ? document.activeElement === first : document.activeElement === last) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      }
    });
    if (first) first.focus();
  }

  /* ============================================================
     HEADER — scroll & transparency
     ============================================================ */
  const header = $('.site-header');
  if (header) {
    const SCROLL_THRESHOLD = 60;
    let ticking = false;

    function updateHeader() {
      header.classList.toggle('is-scrolled', window.scrollY > SCROLL_THRESHOLD);
      ticking = false;
    }

    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(updateHeader);
        ticking = true;
      }
    }, { passive: true });

    updateHeader();
  }

  /* ============================================================
     MOBILE NAVIGATION
     ============================================================ */
  const mobileNav = $('#MobileNav');
  const mobileNavOverlay = $('#MobileNavOverlay');
  const mobileNavClose = $('#MobileNavClose');
  const hamburger = $('#Hamburger');

  function openMobileNav() {
    if (!mobileNav) return;
    mobileNav.classList.add('is-open');
    mobileNav.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    trapFocus(mobileNav);
  }

  function closeMobileNav() {
    if (!mobileNav) return;
    mobileNav.classList.remove('is-open');
    mobileNav.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    hamburger?.focus();
  }

  hamburger?.addEventListener('click', openMobileNav);
  mobileNavClose?.addEventListener('click', closeMobileNav);
  mobileNavOverlay?.addEventListener('click', closeMobileNav);

  // Submenu toggles
  $$('.mobile-nav__toggle').forEach(btn => {
    btn.addEventListener('click', () => {
      const expanded = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', String(!expanded));
      const submenu = btn.nextElementSibling;
      submenu?.classList.toggle('is-open', !expanded);
    });
  });

  /* ============================================================
     CART DRAWER
     ============================================================ */
  const cartDrawer = $('#CartDrawer');
  const cartDrawerOverlay = $('#CartDrawerOverlay');
  const cartDrawerClose = $('#CartDrawerClose');

  function openCartDrawer() {
    if (!cartDrawer) return;
    cartDrawer.classList.add('is-open');
    cartDrawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    trapFocus(cartDrawer);
    refreshCart();
  }

  function closeCartDrawer() {
    if (!cartDrawer) return;
    cartDrawer.classList.remove('is-open');
    cartDrawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  cartDrawerClose?.addEventListener('click', closeCartDrawer);
  cartDrawerOverlay?.addEventListener('click', closeCartDrawer);

  // Open cart on cart icon click
  $$('[data-open-cart]').forEach(el => {
    el.addEventListener('click', e => {
      e.preventDefault();
      openCartDrawer();
    });
  });

  // Escape key closes drawers
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') {
      closeCartDrawer();
      closeMobileNav();
    }
  });

  /* ============================================================
     CART API
     ============================================================ */
  async function fetchCart() {
    const res = await fetch('/cart.js');
    return res.json();
  }

  async function addToCart(variantId, quantity = 1, properties = {}) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity, properties })
    });
    if (!res.ok) throw new Error('Failed to add to cart');
    return res.json();
  }

  async function updateCartItem(key, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
    return res.json();
  }

  async function refreshCart() {
    try {
      const cart = await fetchCart();
      renderCartDrawer(cart);
      updateCartCount(cart.item_count);
    } catch (err) {
      console.error('Cart refresh failed:', err);
    }
  }

  function updateCartCount(count) {
    $$('.cart-count').forEach(el => {
      el.textContent = count > 0 ? count : '';
    });
  }

  function renderCartDrawer(cart) {
    const body = $('#CartDrawerBody');
    const footer = $('#CartDrawerFooter');
    const total = $('#CartDrawerTotal');
    if (!body) return;

    if (cart.item_count === 0) {
      body.innerHTML = `
        <div class="cart-drawer__empty-state">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
            <circle cx="24" cy="24" r="23" stroke="#C9A55A" stroke-width="1"/>
            <path d="M14 18h20l-2 14H16L14 18z" stroke="#2F6171" stroke-width="1.2" fill="none"/>
            <path d="M19 18v-3a5 5 0 0110 0v3" stroke="#2F6171" stroke-width="1.2" fill="none"/>
          </svg>
          <p class="cart-drawer__empty-title">Your cart is empty</p>
          <p class="cart-drawer__empty-text">Discover pieces made to be treasured.</p>
          <a href="/collections/all" class="btn btn--primary">Shop Collection</a>
        </div>`;
      if (footer) footer.style.display = 'none';
      return;
    }

    body.innerHTML = cart.items.map(item => `
      <div class="cart-item" data-key="${item.key}">
        <img class="cart-item__image" src="${item.image}" alt="${item.product_title}" loading="lazy">
        <div class="cart-item__details">
          <p class="cart-item__title">${item.product_title}</p>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-item__variant">${item.variant_title}</p>` : ''}
          <div class="cart-item__price-row">
            <span>${formatMoney(item.final_line_price)}</span>
            <button class="cart-item__remove" data-remove="${item.key}">Remove</button>
          </div>
        </div>
      </div>
    `).join('');

    if (footer) footer.style.display = 'flex';
    if (total) total.textContent = formatMoney(cart.total_price);

    // Remove buttons
    $$('[data-remove]', body).forEach(btn => {
      btn.addEventListener('click', async () => {
        await updateCartItem(btn.dataset.remove, 0);
        refreshCart();
      });
    });
  }

  // Initial cart count
  fetchCart().then(cart => updateCartCount(cart.item_count)).catch(() => {});

  /* ============================================================
     ADD TO CART FORMS
     ============================================================ */
  $$('[data-product-form]').forEach(form => {
    form.addEventListener('submit', async e => {
      e.preventDefault();
      const btn = form.querySelector('[data-atc-btn]');
      const variantInput = form.querySelector('[name="id"]');
      if (!variantInput) return;

      const originalText = btn?.textContent;
      if (btn) {
        btn.disabled = true;
        btn.textContent = 'Adding...';
      }

      try {
        await addToCart(variantInput.value, 1);
        if (btn) btn.textContent = 'Added!';
        await refreshCart();
        openCartDrawer();
        setTimeout(() => {
          if (btn) {
            btn.disabled = false;
            btn.textContent = originalText;
          }
        }, 1800);
      } catch {
        if (btn) {
          btn.disabled = false;
          btn.textContent = 'Sold Out';
        }
      }
    });
  });

  // Quick add buttons (product cards)
  $$('[data-quick-add]').forEach(btn => {
    btn.addEventListener('click', async e => {
      e.preventDefault();
      const variantId = btn.dataset.quickAdd;
      if (!variantId) return;
      const originalText = btn.textContent;
      btn.disabled = true;
      btn.textContent = '...';
      try {
        await addToCart(variantId, 1);
        btn.textContent = 'Added!';
        await refreshCart();
        openCartDrawer();
        setTimeout(() => { btn.disabled = false; btn.textContent = originalText; }, 1800);
      } catch {
        btn.disabled = false;
        btn.textContent = 'Unavailable';
      }
    });
  });

  /* ============================================================
     PRODUCT PAGE
     ============================================================ */
  // Gallery thumbnail switching
  $$('.product-gallery__thumb').forEach(thumb => {
    thumb.addEventListener('click', () => {
      const mainImg = $('.product-gallery__main-image');
      const src = thumb.dataset.src;
      if (mainImg && src) {
        mainImg.src = src;
        $$('.product-gallery__thumb').forEach(t => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
      }
    });
  });

  // Variant selection
  $$('.variant-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const group = btn.closest('[data-option-group]');
      if (group) $$('.variant-btn', group).forEach(b => b.classList.remove('is-active'));
      btn.classList.add('is-active');

      const label = btn.closest('[data-variant-section]')?.querySelector('.variant-label__selected');
      if (label) label.textContent = btn.textContent.trim();

      updateVariantHiddenInput();
    });
  });

  function updateVariantHiddenInput() {
    // Simplified: in real Shopify theme, cross-reference option selections to find matching variant
    const selected = $$('.variant-btn.is-active');
    const values = selected.map(b => b.dataset.value || b.textContent.trim());
    const hiddenInput = $('[name="id"]');
    // Find matching variant from variants JSON
    const variantsData = window.ShesGolden?.variants;
    if (!variantsData || !hiddenInput) return;
    const match = variantsData.find(v =>
      v.options.every((o, i) => o === values[i])
    );
    if (match) {
      hiddenInput.value = match.id;
      updatePrice(match);
    }
  }

  function updatePrice(variant) {
    const priceEl = $('.product-info__price-current');
    const origEl = $('.product-info__price-original');
    if (priceEl) priceEl.textContent = formatMoney(variant.price);
    if (origEl && variant.compare_at_price > variant.price) {
      origEl.textContent = formatMoney(variant.compare_at_price);
      origEl.style.display = 'inline';
    } else if (origEl) {
      origEl.style.display = 'none';
    }
  }

  // Quantity input
  $$('.quantity-input').forEach(widget => {
    const field = widget.querySelector('.quantity-input__field');
    const dec = widget.querySelector('[data-qty-dec]');
    const inc = widget.querySelector('[data-qty-inc]');
    dec?.addEventListener('click', () => { field.value = Math.max(1, +field.value - 1); });
    inc?.addEventListener('click', () => { field.value = +field.value + 1; });
  });

  // Product tabs
  $$('.product-tab-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const tabId = btn.dataset.tab;
      const container = btn.closest('.product-tabs');
      if (!container) return;
      $$('.product-tab-btn', container).forEach(b => b.classList.remove('is-active'));
      $$('.product-tab-content', container).forEach(c => c.classList.remove('is-active'));
      btn.classList.add('is-active');
      $(`#${tabId}`)?.classList.add('is-active');
    });
  });

  /* ============================================================
     ANNOUNCEMENT BAR — pause on reduced motion
     ============================================================ */
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    $$('.announcement-bar__track').forEach(t => { t.style.animation = 'none'; });
  }

  /* ============================================================
     INTERSECTION OBSERVER — scroll animations
     ============================================================ */
  if ('IntersectionObserver' in window && document.body.classList.contains('animations-enabled')) {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });

    $$('[data-animate]').forEach(el => observer.observe(el));
  } else {
    $$('[data-animate]').forEach(el => el.classList.add('is-visible'));
  }

  /* ============================================================
     COLLECTION — filter toggles (client-side for demo)
     ============================================================ */
  $$('.filter-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      btn.classList.toggle('is-active');
    });
  });

  /* ============================================================
     SEARCH — toggle
     ============================================================ */
  const searchToggle = $('#SearchToggle');
  const searchDrawer = $('#SearchDrawer');

  searchToggle?.addEventListener('click', () => {
    const isOpen = searchDrawer?.classList.toggle('is-open');
    searchDrawer?.setAttribute('aria-hidden', String(!isOpen));
    if (isOpen) {
      searchDrawer.querySelector('input')?.focus();
    }
  });

})();
